// src/pages/ManageGames.tsx - Read-only game information display
import React, {useState} from 'react';
import {useMutation, useQuery} from '@tanstack/react-query';
import {supabase} from '@/services/supabase';
import {LoadingSpinner, Badge} from '@/components/ui';
import {
    Gamepad2,
    ExternalLink,
    Globe,
    Database,
    Info,
    Calendar,
    Users,
    Shield,
    Code, Play
} from 'lucide-react';
import {useAuth} from '@/context/AuthContext';
import {isSuperAdmin} from '@/utils/auth';
import {formatDate} from '@/utils/formatters';
import toast from "react-hot-toast";

interface GameInstance {
    id: string;
    name: string;
    display_name: string;
    description: string | null;
    url: string;
    thumbnail: string | null;
    active: boolean;
    supabase_url: string;
    created_at: string;
    updated_at: string;
    created_by: string | null;
}

const GameInstances: React.FC = () => {
    const {user} = useAuth();

    const [accessModal, setAccessModal] = useState<{
        gameId: string;
        gameName: string;
        gameDisplayName: string
    } | null>(null);

    // Game access mutation for super admins
    const accessGame = useMutation({
        mutationFn: async ({gameName, userType}: {
            gameName: string;
            userType: 'academic' | 'business' | 'omep'
        }) => {
            const {data, error} = await supabase.functions.invoke('manage-game-access', {
                body: {
                    action: 'grant-access',
                    gameName: gameName,
                    userTypeOverride: userType
                }
            });

            if (error) throw new Error(error.message || 'Failed to access game');
            if (data?.error) throw new Error(data.error);
            if (!data?.data?.loginUrl) throw new Error('Failed to generate game access link');

            return data.data;
        },
        onSuccess: (data) => {
            window.open(data.loginUrl, '_blank', 'noopener,noreferrer');
            setAccessModal(null);
        },
        onError: (error) => {
            toast.error(`Failed to access game: ${error.message}`);
        }
    });

    // Check if user is super admin
    if (!isSuperAdmin(user)) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2">
                        <Shield className="h-5 w-5 text-red-500"/>
                        <p className="text-red-700">
                            Access denied. Only super administrators can view game configurations.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Fetch games
    const {data: games = [], isLoading, error} = useQuery<GameInstance[]>({
        queryKey: ['game-instances'],
        queryFn: async (): Promise<GameInstance[]> => {
            const {data, error} = await supabase
                .from('game_instances')
                .select('*')
                .order('created_at', {ascending: false});

            if (error) {
                throw new Error(`Failed to fetch games: ${error.message}`);
            }

            return data || [];
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

    if (isLoading) {
        return (
            <div className="p-6 flex items-center justify-center">
                <LoadingSpinner size="lg"/>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2">
                        <Info className="h-5 w-5 text-red-500"/>
                        <p className="text-red-700">
                            Failed to load games: {error.message}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="border-b border-gray-200 pb-6">
                <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-3">
                    <Gamepad2 className="h-8 w-8 text-blue-600"/>
                    <span>Game Instances</span>
                </h1>
                <p className="mt-2 text-gray-600">
                    View configured game instances and their connection details
                </p>
            </div>

            {/* Games Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <Gamepad2 className="h-8 w-8 text-blue-600"/>
                        </div>
                        <div className="ml-4">
                            <p className="text-2xl font-semibold text-gray-900">{games.length}</p>
                            <p className="text-sm text-gray-500">Total Games</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <Users className="h-8 w-8 text-green-600"/>
                        </div>
                        <div className="ml-4">
                            <p className="text-2xl font-semibold text-gray-900">
                                {games.filter(g => g.active).length}
                            </p>
                            <p className="text-sm text-gray-500">Active Games</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <Database className="h-8 w-8 text-purple-600"/>
                        </div>
                        <div className="ml-4">
                            <p className="text-2xl font-semibold text-gray-900">
                                {new Set(games.map(g => g.supabase_url)).size}
                            </p>
                            <p className="text-sm text-gray-500">Unique Databases</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Games List */}
            {games.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {games.map((game) => (
                        <div
                            key={game.id}
                            className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200"
                        >
                            {/* Game Header */}
                            <div className="p-6 border-b border-gray-100">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-3 mb-2">
                                            <h3 className="text-lg font-semibold text-gray-900">
                                                {game.display_name}
                                            </h3>
                                            <Badge
                                                variant={game.active ? 'success' : 'default'}
                                                className="text-xs"
                                            >
                                                {game.active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </div>
                                        <p className="text-sm text-gray-500 font-mono">
                                            {game.name}
                                        </p>
                                    </div>

                                    {/* Game Thumbnail */}
                                    <div className="ml-4 flex-shrink-0">
                                        {game.thumbnail ? (
                                            <img
                                                src={game.thumbnail}
                                                alt={game.display_name}
                                                className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                                            />
                                        ) : (
                                            <div
                                                className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center border border-gray-200">
                                                <Gamepad2 className="h-8 w-8 text-gray-400"/>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Description */}
                                {game.description && (
                                    <p className="text-gray-600 text-sm mt-3 line-clamp-2">
                                        {game.description}
                                    </p>
                                )}
                            </div>

                            {/* Game Details */}
                            <div className="p-6 space-y-4">
                                {/* URLs */}
                                <div className="space-y-3">
                                    <div className="flex items-center space-x-2 text-sm">
                                        <Globe className="h-4 w-4 text-gray-400 flex-shrink-0"/>
                                        <span className="text-gray-500 min-w-0 flex-shrink-0">Game URL:</span>
                                        <a
                                            href={game.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:text-blue-800 truncate flex items-center space-x-1 min-w-0"
                                            title={game.url}
                                        >
                                            <span className="truncate">{game.url}</span>
                                            <ExternalLink className="h-3 w-3 flex-shrink-0"/>
                                        </a>
                                    </div>

                                    <div className="flex items-start space-x-2 text-sm">
                                        <Database className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5"/>
                                        <span className="text-gray-500 min-w-0 flex-shrink-0">Database:</span>
                                        <span className="text-gray-700 font-mono text-xs break-all">
                                            {game.supabase_url}
                                        </span>
                                    </div>
                                </div>

                                {/* Super Admin Access Button */}
                                {user?.role === 'super_admin' && game.active && (
                                    <div className="pt-4 border-t border-gray-100">
                                        <button
                                            onClick={() => setAccessModal({
                                                gameId: game.id,
                                                gameName: game.name,
                                                gameDisplayName: game.display_name
                                            })}
                                            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center justify-center space-x-2"
                                            disabled={accessGame.isPending}
                                        >
                                            <Play className="h-4 w-4"/>
                                            <span>Access Game</span>
                                        </button>
                                    </div>
                                )}

                                {/* Metadata */}
                                <div className="pt-4 border-t border-gray-100">
                                    <div className="grid grid-cols-1 gap-2 text-xs text-gray-500">
                                        <div className="flex items-center space-x-2">
                                            <Calendar className="h-3 w-3"/>
                                            <span>Created: {formatDate(game.created_at)}</span>
                                        </div>
                                        {game.updated_at !== game.created_at && (
                                            <div className="flex items-center space-x-2">
                                                <Calendar className="h-3 w-3"/>
                                                <span>Updated: {formatDate(game.updated_at)}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                /* Empty State */
                <div className="text-center py-12">
                    <div className="mx-auto h-24 w-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                        <Gamepad2 className="h-12 w-12 text-gray-400"/>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                        No games configured
                    </h3>
                    <p className="text-gray-500 mb-6 max-w-md mx-auto">
                        No game instances have been configured yet. Game configuration requires database
                        setup and code deployment.
                    </p>
                    <div
                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-gray-50">
                        <Code className="h-4 w-4 mr-2"/>
                        Contact Development Team
                    </div>
                </div>
            )}

            {/* ✅ Access Game Modal */}
            {accessModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div
                        className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        {/* Background overlay */}
                        <div
                            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                            onClick={() => setAccessModal(null)}
                        />

                        {/* Modal content */}
                        <div
                            className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div
                                        className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                                        <Gamepad2 className="h-6 w-6 text-blue-600"/>
                                    </div>
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                                            Access {accessModal.gameDisplayName}
                                        </h3>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                Choose your user experience mode:
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-6 space-y-3">
                                    <button
                                        onClick={() => accessGame.mutate({
                                            gameName: accessModal.gameName,
                                            userType: 'academic'
                                        })}
                                        disabled={accessGame.isPending}
                                        className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        🎓 Academic
                                    </button>
                                    <button
                                        onClick={() => accessGame.mutate({
                                            gameName: accessModal.gameName,
                                            userType: 'business'
                                        })}
                                        disabled={accessGame.isPending}
                                        className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        🏢 Business
                                    </button>
                                    <button
                                        onClick={() => accessGame.mutate({
                                            gameName: accessModal.gameName,
                                            userType: 'omep'
                                        })}
                                        disabled={accessGame.isPending}
                                        className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        🌟 Business + 1.5
                                    </button>
                                </div>
                            </div>

                            {/* Modal footer */}
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    onClick={() => setAccessModal(null)}
                                    disabled={accessGame.isPending}
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GameInstances;
